import { useMemo } from 'react'
import { useReselector } from '@/utils/sharedHooks'
import { selectIsFetchingForKeys } from '@/reducers/fetch'

import { ChecklistOpenHeader, ChecklistDoneHeader } from './ChecklistHeaders'
import { LatePaymentRiskCallout } from '../LatePaymentRiskCallout'
import { Loader } from '@/ui'
import {
  UpdateTaxInfoCTA,
  ReviewTxnCTA,
  BooksStatus,
  BooksIncompleteInfo,
  UpdateQTEPaymentsCTA,
  UploadNeededBankStatementsCTA,
} from './ChecklistItems'
import { selectTransactionsNeedReviewForQTE } from '@/features/transactions/transactions.selectors'
import { selectIsInUnservicedFreeTrial } from '@/selectors/user.selectors'
import { selectForQTEAllBooksReconciledForRequiredMonths } from '@/selectors/bookkeepingReportsSelectors'
import {
  selectAllQTEChecklistItemsComplete,
  selectAreAllEstimatesPaidSoFar,
  selectTaxProfileReviewedInRequiredRangeForQTE,
} from '../../userTaxEstimates.selector'
import { USER_TRANSACTION_KEY } from '@/features/transactions/transactions.slice'
import { FETCH_BOOKKEEPING_REPORT_KEY } from '@/actions/bookkeepingReportActions'
import { ALWAYS_FETCH_USER_TAX_ESTIMATES_KEY } from '../../userTaxEstimates.slice'

import { FETCH_ALL_QUARTERLY_TAX_ESTIMATION_DETAILS_KEY } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'
import { selectQTEChecklistStatusAndDates } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'

export const QTEChecklist = () => {
  const checklistStatus = useReselector(selectQTEChecklistStatusAndDates)
  const { rest: activeQTEDetails } = checklistStatus
  const checklistComplete = useReselector(
    selectAllQTEChecklistItemsComplete,
    activeQTEDetails?.taxYear
  )
  const updateTaxInfoIsDone = useReselector(
    selectTaxProfileReviewedInRequiredRangeForQTE
  )
  const { allReviewed, count } = useReselector(
    selectTransactionsNeedReviewForQTE
  )
  const { allRequiredBooksAreReconciled } = useReselector(
    selectForQTEAllBooksReconciledForRequiredMonths
  )
  const allEstimatesPaid = useReselector(
    selectAreAllEstimatesPaidSoFar,
    activeQTEDetails?.taxYear
  )
  // Estimates are watched under the key the page loads them with, not
  // FETCH_TAX_ESTIMATES_KEY: a descendant starts that fetch when a step opens,
  // which would unmount the card holding the step's dialog state.
  const fetching = useReselector(selectIsFetchingForKeys, [
    USER_TRANSACTION_KEY,
    FETCH_BOOKKEEPING_REPORT_KEY,
    ALWAYS_FETCH_USER_TAX_ESTIMATES_KEY,
    FETCH_ALL_QUARTERLY_TAX_ESTIMATION_DETAILS_KEY,
  ])
  const isFetching = fetching || !activeQTEDetails
  const isInUnservicedFreeTrial = useReselector(selectIsInUnservicedFreeTrial)

  // The estimates term mirrors UpdateQTEPaymentsCTA's own Q1 condition:
  // selectAreAllEstimatesPaidSoFar is false on a year with no rows, which would
  // otherwise report an item the reader has no checklist row for.
  const hasOutstandingUserItem =
    !updateTaxInfoIsDone ||
    (Number(activeQTEDetails?.taxQuarter) !== 1 && !allEstimatesPaid) ||
    !allReviewed

  const showLatePaymentCallout =
    !isFetching &&
    !checklistComplete &&
    !isInUnservicedFreeTrial &&
    hasOutstandingUserItem

  const headerContent = useMemo(() => {
    if (isFetching) {
      return (
        <div className="flex justify-center">
          <Loader />
        </div>
      )
    } else if (checklistComplete) {
      return <ChecklistDoneHeader />
    }
    return <ChecklistOpenHeader />
  }, [checklistComplete, isFetching])

  const contents = useMemo(() => {
    if (
      isFetching ||
      (checklistComplete &&
        activeQTEDetails.now >
          activeQTEDetails.withOffset.initialCalculationLock)
    ) {
      return null
    }
    return (
      <>
        <UpdateTaxInfoCTA isDone={Boolean(updateTaxInfoIsDone)} />

        {Number(activeQTEDetails.taxQuarter) !== 1 && (
          <UpdateQTEPaymentsCTA isDone={allEstimatesPaid} />
        )}

        <ReviewTxnCTA isDone={allReviewed} count={count} />

        <UploadNeededBankStatementsCTA />

        {allReviewed && (
          <>
            <BooksStatus booksReconciled={allRequiredBooksAreReconciled} />

            {!allRequiredBooksAreReconciled && <BooksIncompleteInfo />}
          </>
        )}
      </>
    )
  }, [
    isFetching,
    checklistComplete,
    activeQTEDetails?.now,
    activeQTEDetails?.withOffset.initialCalculationLock,
    activeQTEDetails?.taxQuarter,
    updateTaxInfoIsDone,
    allEstimatesPaid,
    allReviewed,
    count,
    allRequiredBooksAreReconciled,
  ])

  return (
    <div className="flex flex-col gap-2">
      {showLatePaymentCallout && (
        <LatePaymentRiskCallout className="mb-4">
          Complete the tasks below to get your estimated payment amount.
        </LatePaymentRiskCallout>
      )}
      <div className="flex-1">{headerContent}</div>
      {contents}
    </div>
  )
}
